import React from 'react';
import { Badge, Title, Text, HoverCard, Loader, Tooltip } from '@mantine/core';
import {
    IconChevronDown,
    IconMapPin,
    IconAlertTriangle,
    IconChevronUp,
} from '@tabler/icons-react';

import { When } from '../common';
import { Request } from '../../codegen/graphql';
import formatDate from '../../utility/formatDate';
import { useRequestDrawer } from '../../context/RequestDrawerContext';
import { hasRequestAdvertisingZoneName } from './requestLocation';
import { PriceChangeScopeBadge } from './PriceChangeScopeBadge';

type Props = {
    flatRequest: Request | null;
};

function RequestDetails({ flatRequest }: Props) {
    const { adZoneStores, adZoneLoading } = useRequestDrawer();

    if (!flatRequest) {
        return null;
    }

    const isIncrease =
        (flatRequest.proposedPrice ?? 0) > (flatRequest.currentPrice ?? 0);
    const isUrgent = flatRequest.isUrgent ?? false;

    const hasZoneName = hasRequestAdvertisingZoneName(
        flatRequest.advertisingZoneName,
    );
    const showZoneColumn =
        hasZoneName || (!flatRequest.isFromPriceSurvey && !flatRequest.storeId);
    const showStoreColumn = !showZoneColumn;
    const hasMiddleColumn = showZoneColumn || showStoreColumn;
    const storeName = flatRequest.storeName?.trim() || '';

    return (
        <div className="flex min-w-0 w-full max-w-full flex-col flex-1 gap-5">
            <Title order={4}>Request Details</Title>
            <div className="flex w-full min-w-0 flex-nowrap items-stretch">
                <div className="flex min-w-0 flex-1 flex-col gap-3 px-4 pb-3">
                    <Text className="text-xs text-[#7d8fb3] font-bold uppercase">
                        Request
                    </Text>
                    <Badge
                        leftSection={
                            isIncrease ? (
                                <IconChevronUp size={16} className="mt-1" />
                            ) : (
                                <IconChevronDown size={16} className="mt-1" />
                            )
                        }
                        color={isIncrease ? 'green' : 'orange'}
                        variant="light"
                    >
                        {isIncrease ? 'Increase' : 'Decrease'}
                    </Badge>
                </div>
                <div
                    className="w-0.5 flex-shrink-0 self-stretch bg-[#EDEFF2]"
                    aria-hidden
                />
                <div className="flex min-w-0 flex-1 flex-col gap-3 px-4 pb-3">
                    <Text className="text-xs text-[#7d8fb3] font-bold uppercase">
                        Requested
                    </Text>
                    <Text className="text-xs text-[#4D5E80] uppercase">
                        {formatDate(new Date(flatRequest.createdAt))}
                    </Text>
                </div>
                <div
                    className="w-0.5 flex-shrink-0 self-stretch bg-[#EDEFF2]"
                    aria-hidden
                />
                <When condition={showZoneColumn}>
                    <div className="flex min-w-0 flex-1">
                        <HoverCard radius="lg">
                            <HoverCard.Target>
                                <div className="flex w-full min-w-0 cursor-default flex-col gap-3 px-4 pb-3">
                                    <Text className="text-xs text-[#7d8fb3] font-bold uppercase">
                                        Zone
                                    </Text>
                                    <div className="flex items-center gap-2">
                                        <IconMapPin
                                            size={18}
                                            className="flex-shrink-0"
                                        />
                                        <Text className="min-w-0 truncate text-xs uppercase text-[#4D5E80]">
                                            {flatRequest.advertisingZoneName ||
                                                'N/A'}
                                        </Text>
                                    </div>
                                </div>
                            </HoverCard.Target>
                            <HoverCard.Dropdown className="w-full">
                                <When condition={adZoneLoading}>
                                    <Loader size="sm" />
                                </When>
                                <When condition={!adZoneLoading}>
                                    {adZoneStores?.map(
                                        (store: {
                                            id: string;
                                            name?: string | null;
                                            storeNo?: string | null;
                                        }) => (
                                            <div
                                                key={store.id}
                                                className="mt-2 flex flex-1 items-center justify-between gap-2"
                                            >
                                                <div className="flex items-center">
                                                    <IconMapPin size={18} />
                                                    <Text className="ml-1 text-xs uppercase text-[#4D5E80]">
                                                        {store?.name || 'N/A'}
                                                    </Text>
                                                </div>
                                                <Badge size="sm">
                                                    {store?.storeNo || 'N/A'}
                                                </Badge>
                                            </div>
                                        ),
                                    )}
                                </When>
                            </HoverCard.Dropdown>
                        </HoverCard>
                    </div>
                </When>
                <When condition={showStoreColumn}>
                    <div className="flex min-w-0 flex-1 flex-col gap-3 px-4 pb-3">
                        <Text className="text-xs text-[#7d8fb3] font-bold uppercase">
                            Store
                        </Text>
                        <div className="flex min-w-0 items-center gap-2">
                            <IconMapPin size={18} className="flex-shrink-0" />
                            <Tooltip
                                label={storeName}
                                disabled={!storeName}
                                openDelay={100}
                                zIndex={2000}
                                withinPortal
                                multiline
                                maw={360}
                            >
                                <div className="min-w-0 truncate">
                                    <Text className="truncate text-xs uppercase text-[#4D5E80]">
                                        {storeName || 'N/A'}
                                    </Text>
                                </div>
                            </Tooltip>
                            <Badge
                                size="sm"
                                variant="light"
                                className="flex-shrink-0"
                            >
                                {flatRequest.storeNo || 'N/A'}
                            </Badge>
                        </div>
                    </div>
                </When>
                <When condition={hasMiddleColumn}>
                    <div
                        className="w-0.5 flex-shrink-0 self-stretch bg-[#EDEFF2]"
                        aria-hidden
                    />
                </When>
                <div className="flex min-w-0 flex-1 flex-col gap-3 px-4 pb-3">
                    <Text className="text-xs text-[#7d8fb3] font-bold uppercase">
                        Scope
                    </Text>
                    <PriceChangeScopeBadge request={flatRequest} size="sm" />
                </div>
                <div
                    className="w-0.5 flex-shrink-0 self-stretch bg-[#EDEFF2]"
                    aria-hidden
                />
                <div className="flex min-w-0 flex-1 flex-col gap-3 px-4 pb-3">
                    <Text className="text-xs text-[#7d8fb3] font-bold uppercase">
                        Response Date
                    </Text>
                    <div className="flex gap-2 items-center">
                        <When condition={isUrgent}>
                            <IconAlertTriangle size={18} color="red" />
                        </When>

                        <Text className="text-xs text-[#4D5E80] uppercase">
                            {flatRequest.dueBy
                                ? formatDate(new Date(flatRequest.dueBy))
                                : 'N/A'}
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RequestDetails;
